/**
 * SARKY full-screen search discovery layer.
 *
 * Adds client-local recent searches and lets the curated TOP SEARCHES
 * controls feed the existing Shopify predictive-search component. Search
 * history never leaves the browser except when the shopper deliberately
 * runs that search through Shopify's normal search endpoint.
 */
(function () {
  var STORAGE_KEY = 'sarkyRecentSearches:v1';
  var MAX_RECENT_SEARCHES = 5;

  function readRecentSearches() {
    try {
      var parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(parsed) ? parsed.filter(function (term) { return typeof term === 'string'; }) : [];
    } catch (error) {
      return [];
    }
  }

  function writeRecentSearches(terms) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(terms));
    } catch (error) {
      // Search remains fully functional when storage is disabled.
    }
  }

  function rememberSearch(term) {
    var normalized = String(term || '').trim();
    if (!normalized) return;

    var terms = readRecentSearches().filter(function (existing) {
      return existing.toLocaleLowerCase() !== normalized.toLocaleLowerCase();
    });

    terms.unshift(normalized);
    writeRecentSearches(terms.slice(0, MAX_RECENT_SEARCHES));
  }

  function renderRecentSearches(root) {
    var container = root.querySelector('[data-sarky-recent-searches]');
    if (!container) return;

    var terms = readRecentSearches();
    container.replaceChildren();

    if (terms.length === 0) {
      var empty = document.createElement('span');
      empty.textContent = 'No recent searches';
      container.appendChild(empty);
      return;
    }

    terms.forEach(function (term) {
      var button = document.createElement('button');
      button.type = 'button';
      button.dataset.sarkySearchTerm = term;
      button.textContent = term;
      container.appendChild(button);
    });
  }

  function runPredictiveSearch(root, term) {
    var input = root.querySelector('[ref="searchInput"]');
    if (!input) return;

    input.value = term;
    input.focus();
    input.dispatchEvent(new InputEvent('input', {
      bubbles: true,
      data: term,
      inputType: 'insertText'
    }));
  }

  /**
   * Product cards contain a full-card link. On iOS that link can claim a
   * horizontal touch before the nested overflow rail starts scrolling. The
   * card interaction styles are now neutralized in CSS, so touch is deliberately
   * left to the browser's native momentum scroller. This helper only supplies
   * click-and-drag for a desktop mouse.
   */
  function initProductDrag(root) {
    var gesture = null;
    var momentumFrame = null;
    var suppressClickUntil = 0;
    var TRACK_SELECTOR = '.predictive-search-results__wrapper-products';

    function closestTrack(target) {
      if (!(target instanceof Element)) return null;
      return target.closest(TRACK_SELECTOR);
    }

    function stopMomentum() {
      if (momentumFrame !== null) {
        window.cancelAnimationFrame(momentumFrame);
        momentumFrame = null;
      }
    }

    function settleWithMomentum(track, velocity) {
      var maxScrollLeft = Math.max(0, track.scrollWidth - track.clientWidth);
      var target = Math.max(0, Math.min(maxScrollLeft, track.scrollLeft + velocity * 180));

      if (Math.abs(target - track.scrollLeft) < 2) return;

      var start = track.scrollLeft;
      var distance = target - start;
      var startedAt = performance.now();
      var duration = Math.min(420, Math.max(180, Math.abs(distance) * 1.6));

      function animate(now) {
        var progress = Math.min(1, (now - startedAt) / duration);
        var eased = 1 - Math.pow(1 - progress, 3);
        track.scrollLeft = start + distance * eased;

        if (progress < 1) {
          momentumFrame = window.requestAnimationFrame(animate);
        } else {
          momentumFrame = null;
        }
      }

      momentumFrame = window.requestAnimationFrame(animate);
    }

    root.addEventListener('pointerdown', function (event) {
      var track = closestTrack(event.target);
      if (!track || track.scrollWidth <= track.clientWidth || !event.isPrimary) return;
      if (event.pointerType !== 'mouse' || event.button !== 0) return;

      stopMomentum();
      gesture = {
        track: track,
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        startScrollLeft: track.scrollLeft,
        lastScrollLeft: track.scrollLeft,
        lastTime: performance.now(),
        velocity: 0,
        direction: '',
        moved: false
      };
    });

    root.addEventListener('pointermove', function (event) {
      if (!gesture || event.pointerId !== gesture.pointerId) return;

      var distanceX = event.clientX - gesture.startX;
      var distanceY = event.clientY - gesture.startY;

      if (!gesture.direction) {
        if (Math.max(Math.abs(distanceX), Math.abs(distanceY)) < 6) return;
        gesture.direction = Math.abs(distanceX) > Math.abs(distanceY) * 1.1 ? 'horizontal' : 'vertical';
      }

      if (gesture.direction !== 'horizontal') return;

      event.preventDefault();

      if (!gesture.moved) {
        gesture.moved = true;
        gesture.track.classList.add('is-dragging');
        try {
          gesture.track.setPointerCapture(gesture.pointerId);
        } catch (error) {
          // Pointer capture is optional; delegated listeners still keep the drag working.
        }
      }

      var now = performance.now();
      var previousScrollLeft = gesture.track.scrollLeft;
      gesture.track.scrollLeft = gesture.startScrollLeft - distanceX;
      var elapsed = Math.max(1, now - gesture.lastTime);
      var instantVelocity = (gesture.track.scrollLeft - previousScrollLeft) / elapsed;
      gesture.velocity = gesture.velocity * 0.65 + instantVelocity * 0.35;
      gesture.lastScrollLeft = gesture.track.scrollLeft;
      gesture.lastTime = now;
    }, { passive: false });

    function finishDrag(event) {
      if (!gesture || event.pointerId !== gesture.pointerId) return;

      var finished = gesture;
      gesture = null;

      if (!finished.moved || finished.direction !== 'horizontal') return;

      finished.track.classList.remove('is-dragging');
      try {
        finished.track.releasePointerCapture(finished.pointerId);
      } catch (error) {
        // The browser may already have released capture at the end of the gesture.
      }

      suppressClickUntil = Date.now() + 500;
      settleWithMomentum(finished.track, finished.velocity);
    }

    root.addEventListener('pointerup', finishDrag);
    root.addEventListener('pointercancel', finishDrag);

    root.addEventListener('click', function (event) {
      if (Date.now() > suppressClickUntil || !closestTrack(event.target)) return;
      suppressClickUntil = 0;
      event.preventDefault();
      event.stopImmediatePropagation();
    }, true);

    root.addEventListener('dragstart', function (event) {
      if (closestTrack(event.target)) event.preventDefault();
    });
  }

  function initSearchOverlay(root) {
    if (root.dataset.sarkySearchReady === 'true') return;
    root.dataset.sarkySearchReady = 'true';

    var form = root.querySelector('.predictive-search-form');
    var input = root.querySelector('[ref="searchInput"]');
    var animatedSearch = root.querySelector('sarky-animated-search');
    var dialog = root.querySelector('dialog');

    function syncAnimatedSearch() {
      if (!animatedSearch) return;
      if (typeof animatedSearch.syncOverlayVisibility === 'function') {
        animatedSearch.syncOverlayVisibility();
      }
      if (!input.value && document.activeElement !== input && typeof animatedSearch.resume === 'function') {
        animatedSearch.resume();
      }
    }

    /* Billy's explicit decisions (superseding earlier rules):
       - TOP SEARCHES/RECENT SEARCHES stay visible at all times, matching
         PepperMayo. There is deliberately no JS or CSS anywhere that
         hides .sarky-search-discovery.
       - The search field's corner radius is now one consistent 5px at
         all times, matching every other SARKY search bar (header,
         drawer) - the earlier idle-pill/focused-rectangle dual state
         and its data-has-query attribute are gone; there is nothing
         left for JS to toggle for the field's shape. */

    renderRecentSearches(root);
    initProductDrag(root);

    root.addEventListener('click', function (event) {
      var resetButton = event.target.closest('.predictive-search__reset-button');
      if (resetButton) {
        window.setTimeout(syncAnimatedSearch, 160);
        return;
      }

      var termButton = event.target.closest('[data-sarky-search-term]');
      if (termButton) {
        var term = termButton.dataset.sarkySearchTerm || termButton.textContent;
        rememberSearch(term);
        renderRecentSearches(root);
        runPredictiveSearch(root, term);
        return;
      }

      var resultLink = event.target.closest('.predictive-search-form__content a');
      if (resultLink && input) rememberSearch(input.value);
    });

    if (form && input) {
      form.addEventListener('submit', function () {
        rememberSearch(input.value);
      });
    }

    if (dialog) {
      dialog.addEventListener('close', function () {
        window.setTimeout(syncAnimatedSearch, 0);
      });
    }
  }

  function init() {
    document.querySelectorAll('#search-modal').forEach(initSearchOverlay);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
